/**
 * Portfolio Gantt aggregation (task_61bjlu). Flattens dated top-level tasks
 * across several projects into one timeline-ready list, grouped by project and
 * ordered by start date, plus a per-task meta map (project id / name / colour)
 * the chart uses to colour bars and resolve the right project on a drag.
 *
 * Pure so it can be unit-tested without a live chart.
 */
export type PortfolioProject = {
  id: string;
  name: string;
  color?: string | null;
};
export type PortfolioTask = {
  id: string;
  title: string;
  parentTaskId?: string | null;
  status: string;
  assigneeIds: string[];
  startDate: string | null;
  dueDate: string | null;
  startLocked?: boolean | number;
};
export type PortfolioMeta = {
  projectId: string;
  projectName: string;
  color: string | null;
};

// Earliest plottable date of a task (start, else due). Tasks with neither have
// no bar and are dropped.
function anchor(t: PortfolioTask): string | null {
  return t.startDate ?? t.dueDate ?? null;
}

export function buildPortfolio(
  projects: PortfolioProject[],
  tasksByProject: Map<string, PortfolioTask[]>,
  // Project ids the user has toggled OFF (hidden). Defaults to none hidden.
  hidden: Set<string> = new Set(),
): { tasks: PortfolioTask[]; meta: Map<string, PortfolioMeta> } {
  const meta = new Map<string, PortfolioMeta>();
  const tasks: PortfolioTask[] = [];
  // Preserve the caller's project order (already its own concern), so bars
  // cluster by project; within a project, order by anchor date.
  for (const p of projects) {
    if (hidden.has(p.id)) continue;
    const own = (tasksByProject.get(p.id) ?? [])
      .filter((t) => t.parentTaskId == null && anchor(t) != null)
      .sort((a, b) =>
        anchor(a)! < anchor(b)! ? -1 : anchor(a)! > anchor(b)! ? 1 : 0,
      );
    for (const t of own) {
      meta.set(t.id, {
        projectId: p.id,
        projectName: p.name,
        color: p.color ?? null,
      });
      tasks.push(t);
    }
  }
  return { tasks, meta };
}
